import html2pdf from 'html2pdf.js';
import { SIZES } from '../firebase/schema';
import { detectSizeGrid, toQuantity } from './sizeGrids';

const PDF_SIZE_GRIDS = {
  ADULT: [...SIZES.ADULT],
  ADULT_EXTENDED: [...SIZES.ADULT_EXTENDED],
  YOUTH: [...SIZES.YOUTH],
  TODDLER: [...SIZES.TODDLER],
  INFANT: [...SIZES.INFANT],
};

const GROUP_LABELS = {
  ADULT: 'Adult Sizes',
  ADULT_EXTENDED: 'Adult Extended Sizes (4XL+)',
  YOUTH: 'Youth Sizes',
  TODDLER: 'Toddler Sizes',
  INFANT: 'Infant Sizes',
};

const groupLinesBySizeGrid = (linesWithOrders) => {
  const groups = { ADULT: [], ADULT_EXTENDED: [], YOUTH: [], TODDLER: [], INFANT: [] };
  linesWithOrders.forEach((item) => {
    const gridType = detectSizeGrid(item.line.sizes);
    groups[gridType].push(item);
  });
  return groups;
};

const generateGroupTableHtml = (groupName, lines, sizeColumns) => {
  if (lines.length === 0) return '';

  return `
    <div style="margin-top: 20px; page-break-inside: avoid;">
      <h3 style="font-size: 11pt; color: #333; margin-bottom: 8px; padding: 5px; background: #e8e8e8; border-radius: 3px;">
        ${GROUP_LABELS[groupName]} (${lines.length} line${lines.length !== 1 ? 's' : ''})
      </h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 10px;">
        <thead>
          <tr style="background: #f5f5f5;">
            <th style="border: 1px solid #ccc; padding: 6px; text-align: left; width: 18%;">Customer</th>
            <th style="border: 1px solid #ccc; padding: 6px; text-align: left; width: 20%;">Design</th>
            <th style="border: 1px solid #ccc; padding: 6px; text-align: left; width: 10%;">Style</th>
            <th style="border: 1px solid #ccc; padding: 6px; text-align: left; width: 14%;">Color</th>
            ${sizeColumns
              .map(
                (size) =>
                  `<th style="border: 1px solid #ccc; padding: 6px; text-align: center; width: 5%;">${size}</th>`
              )
              .join('')}
            <th style="border: 1px solid #ccc; padding: 6px; text-align: center; font-weight: bold; width: 6%;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${lines
            .map((item, index) => {
              const { line, order } = item;
              const styleNumber = (line.garmentId || '').split(' - ')[0];
              const sizes = line.sizes || {};
              const total = Object.values(sizes).reduce((sum, qty) => sum + toQuantity(qty), 0);
              const rowBg = index % 2 === 0 ? '#fff' : '#fafafa';
              return `
                <tr style="background: ${rowBg}; page-break-inside: avoid;">
                  <td style="border: 1px solid #ccc; padding: 5px; font-size: 8pt;">${order?.customerName || 'N/A'}</td>
                  <td style="border: 1px solid #ccc; padding: 5px; font-weight: 500;">${line.designName || 'N/A'}</td>
                  <td style="border: 1px solid #ccc; padding: 5px;">${styleNumber}</td>
                  <td style="border: 1px solid #ccc; padding: 5px;">${line.garmentColor || 'N/A'}</td>
                  ${sizeColumns
                    .map(
                      (size) =>
                        `<td style="border: 1px solid #ccc; padding: 5px; text-align: center;">${sizes[size] || '—'}</td>`
                    )
                    .join('')}
                  <td style="border: 1px solid #ccc; padding: 5px; text-align: center; font-weight: bold;">${total}</td>
                </tr>
              `;
            })
            .join('')}
        </tbody>
      </table>
    </div>
  `;
};

/** Generate and download an incoming-garments summary PDF for the given lines. */
export async function generateOrderingSummaryPdf(linesWithOrders) {
  if (!linesWithOrders?.length) {
    throw new Error('No lines data available for summary PDF');
  }

  const groups = groupLinesBySizeGrid(linesWithOrders);
  const totalPieces = linesWithOrders.reduce((sum, item) => {
    const sizes = item.line.sizes || {};
    return sum + Object.values(sizes).reduce((s, qty) => s + toQuantity(qty), 0);
  }, 0);

  const summaryHtml = `
    <div style="font-family: 'Montserrat', Arial, sans-serif; padding: 20px; color: #222;">
      <h1 style="font-size: 18pt; margin-bottom: 5px; border-bottom: 2px solid #222; padding-bottom: 10px;">
        Incoming Garments Summary
      </h1>
      <p style="font-size: 10pt; color: #666; margin-bottom: 10px;">
        Ordered on ${new Date().toLocaleDateString()} • ${linesWithOrders.length} lines • ${totalPieces.toLocaleString()} total pieces
      </p>
      ${generateGroupTableHtml('ADULT', groups.ADULT, PDF_SIZE_GRIDS.ADULT)}
      ${generateGroupTableHtml('ADULT_EXTENDED', groups.ADULT_EXTENDED, PDF_SIZE_GRIDS.ADULT_EXTENDED)}
      ${generateGroupTableHtml('YOUTH', groups.YOUTH, PDF_SIZE_GRIDS.YOUTH)}
      ${generateGroupTableHtml('TODDLER', groups.TODDLER, PDF_SIZE_GRIDS.TODDLER)}
      ${generateGroupTableHtml('INFANT', groups.INFANT, PDF_SIZE_GRIDS.INFANT)}
      <div style="margin-top: 25px; font-size: 10pt; color: #333; border-top: 2px solid #222; padding-top: 10px;">
        <strong>Grand Total: ${totalPieces.toLocaleString()} pieces</strong>
      </div>
    </div>
  `;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = summaryHtml;
  tempDiv.style.position = 'fixed';
  tempDiv.style.top = '0';
  tempDiv.style.left = '100vw';
  tempDiv.style.width = '8.5in';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.style.zIndex = '-1';
  document.body.appendChild(tempDiv);

  try {
    const contentElement = tempDiv.querySelector('div');
    await html2pdf()
      .set({
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `order_summary_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: tempDiv.scrollWidth,
          windowHeight: tempDiv.scrollHeight,
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      })
      .from(contentElement)
      .save();
  } finally {
    document.body.removeChild(tempDiv);
  }
}
